import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { trackHit } from "@/lib/analytics";

const COUNTER_ID = 106548895;
const CONSENT_KEY = "bpp_cookie_consent";

/**
 * Yandex.Metrika component for React SPA
 * Only initializes after the user accepts cookies (CookieConsent component)
 */
export const YandexMetrika = () => {
  const location = useLocation();
  const [initialized, setInitialized] = useState(false);

  // Check consent & listen for changes
  useEffect(() => {
    const tryInit = () => {
      if (initialized) return;
      const consent = localStorage.getItem(CONSENT_KEY);
      if (consent !== "accepted") return;

      // Use the exact Yandex Metrika snippet pattern
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const w = window as any;
      w.ym = w.ym || function () {
        // eslint-disable-next-line prefer-rest-params
        (w.ym.a = w.ym.a || []).push(arguments);
      };
      w.ym.l = Date.now();

      w.dataLayer = w.dataLayer || [];

      const scriptSrc = "https://mc.yandex.ru/metrika/tag.js";
      const alreadyLoaded = Array.from(document.scripts).some(
        (s: HTMLScriptElement) => s.src.startsWith(scriptSrc)
      );
      if (!alreadyLoaded) {
        const script = document.createElement("script");
        script.async = true;
        script.src = scriptSrc;
        const firstScript = document.getElementsByTagName("script")[0];
        firstScript?.parentNode?.insertBefore(script, firstScript);
      }

      w.ym(COUNTER_ID, "init", {
        clickmap: true,
        trackLinks: true,
        accurateTrackBounce: true,
        webvisor: true,
        ecommerce: "dataLayer",
      });

      console.log("[YM] Counter initialized:", COUNTER_ID);

      // Noscript fallback pixel
      const noscript = document.createElement("noscript");
      const div = document.createElement("div");
      const img = document.createElement("img");
      img.src = `https://mc.yandex.ru/watch/${COUNTER_ID}`;
      img.style.cssText = "position:absolute;left:-9999px;";
      img.alt = "";
      div.appendChild(img);
      noscript.appendChild(div);
      document.body.appendChild(noscript);

      setInitialized(true);
    };

    // Try on mount (user may have already consented)
    tryInit();

    // Listen for consent event from CookieConsent component
    window.addEventListener("cookie-consent-changed", tryInit);
    return () => window.removeEventListener("cookie-consent-changed", tryInit);
  }, [initialized]);

  // Track page views on route change (only if initialized)
  useEffect(() => {
    if (!initialized) return;

    const fullUrl = window.location.origin + location.pathname + location.search;
    const timeoutId = setTimeout(() => {
      trackHit(fullUrl, {
        title: document.title,
        referer: document.referrer,
      });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [location.pathname, location.search, initialized]);

  return null;
};

export default YandexMetrika;
